<template>
  <div class="wt-upload-view">
    <header>
      <span :class="['wticon', currentProvider.icon]">
        <span class="path1" />
        <span class="path2" />
        <span class="path3" />
      </span>
      <div class="wt2-text-l3">{{ currentProvider.title }}</div>
    </header>

    <main>
      <div v-if="isEmpty" class="-panel">
        <div class="-empty-files">
          <div class="-center">
            <span :class="['-a', 'wticon', currentProvider.icon]" />
            <div class="-b wt2-text-m">
              <a href="#" @click.prevent="onBrowse" class="wt2-link"
                >Browse {{ currentProvider.name }}</a
              >
            </div>
            <div class="-c wt2-text-s">
              <a
                href="#"
                class="wt2-link"
                v-tooltip.extend="{
                  title: $options.supportedTooltip,
                  html: true,
                  placement: 'top',
                  delay: 0,
                  wrapperClass: 'file-upload-wrapper',
                }"
                >Supported Formats</a
              >
            </div>
          </div>
        </div>
      </div>
      <div v-else class="-panel -file-panel">
        <div class="-head">
          <div>{{ 'uploadView.filePanelHead' | culture }}</div>
          <a href="#" @click.prevent="onBrowse" class="wt2-link">Browse</a>
        </div>
        <ul class="-file-list">
          <file-list-record
            v-for="file in model.files"
            :key="file.id"
            :file="file"
            @remove-file-record="onRemoveFileRecord"
          />
        </ul>
      </div>

      <aside class="-panel-divider" />
      <div class="-panel">
        <section>
          <wt-toggle
            class="-privacy-label"
            :label-left="'uploadView.privacyLabel' | culture"
            v-model="model.sharedWithTeam"
          />
        </section>
        <section>
          <wt-text-field
            v-model="model.caption"
            :placeholder="'uploadView.captionPlaceholder' | culture"
            :mandatory="false"
            :maxlength="MAX_TITLE_LENGTH"
          />
        </section>
        <section>
          <wt-text-field
            v-model="model.attribution"
            :placeholder="'uploadView.attributionPlaceholder' | culture"
            :mandatory="false"
            :maxlength="MAX_TITLE_CREDIT_LENGTH"
          />
          <wt-check-box
            class="-attribution-box wt2-text-s"
            v-model="model.attributionMode"
            :disabled="!model.attribution"
            :label-right="'uploadView.attributionMode' | culture"
          />
        </section>
        <section>
          <wt-add-keywords
            theme="black-theme"
            :keyword-height="31"
            :maxRows="4"
            :keywords="model.keywords"
            emptyKeywordsPlaceholder="Add tags"
            @addkeyword="addKeyword"
            @removekeyword="removeKeyword"
          />
        </section>
      </div>
    </main>

    <footer>
      <wt-button
        :disabled="denyUpload"
        type="primary"
        text="Upload"
        @click="onDone"
      />
      <wt-button type="transparent" text="Cancel" @click="closeModal" />
    </footer>

    <aside v-show="waiting" class="-waiting">
      <div class="-center">
        {{ 'uploadView.waiting' | culture }}
        <div class="wt-loader" />
      </div>
    </aside>
  </div>
</template>

<script>
import app from 'app';
import UploadMixin from './UploadMixin.vue';
import FileListRecord from './FileListRecord.vue';
import Analytics from 'c/WtAnalytics';

export default {
  mixins: [UploadMixin],
  components: { FileListRecord },

  data() {
    return {
      waiting: false,
      serverError: '',
      model: {
        caption: '',
        files: [],
      },
      currentProvider: {},
      providers: {
        onedrive: {
          title: app.culture.uploadView.title.onedrive,
          name: app.culture.uploadView.providerNames.onedrive,
          icon: 'wticon-onedrive',
          analyticsEvents: {
            done: 'onedrive-upload',
          },
        },
        dropbox: {
          title: app.culture.uploadView.title.dropbox,
          name: app.culture.uploadView.providerNames.dropbox,
          icon: 'wticon-dropbox',
          analyticsEvents: {
            done: 'dropbox-upload',
          },
        },
      },
    };
  },

  props: {
    provider: {
      validator(value) {
        // The value must match one of these strings
        return ['dropbox', 'onedrive'].indexOf(value) !== -1;
      },
    },
    initFiles: {
      type: Array,
      required: false,
    },
  },

  created() {
    Object.assign(this.$options, {
      supportedTooltip: `
            <div class="wt2-text-n2" style="margin-bottom: 5px;">
                ${app.culture.get('uploadView.supportTooltipTitle')}
            </div>
            ${tk.context._supportedFileExtensions.join(', ')}
            <div style="margin-top: 10px;">${app.culture.get(
              'uploadView.supportTooltipFooter'
            )}</div>
        `.normalizeSpace(),
    });

    this.currentProvider = this.providers[this.provider];
  },

  mounted() {
    this.model.files = this.initFiles;
  },

  computed: {
    fileCaption() {
      return this.model.caption.normalizeSpace();
    },

    isEmpty() {
      return _.isEmpty(this.model.files);
    },

    denyUpload() {
      return _.isEmpty(this.model.files);
    },
  },

  methods: {
    onDone() {
      const uqModels = [];
      this.serverError = '';
      this.waiting = true;

      if (this.currentProvider.analyticsEvents.done) {
        Analytics.trigger(this.currentProvider.analyticsEvents.done);
      }

      for (let file, i = 0, I = this.model.files.length; i < I; i++) {
        file = this.model.files[this.model.files.length - i - 1];
        // remove extension
        let title = _.capitalize(file.name.replace(/(.*)\.[^.]+$/g, '$1'));
        if (this.fileCaption.length) {
          if (I > 1) {
            title = `${this.fileCaption}_${i + 1}`;
          } else {
            title = this.fileCaption;
          }
        }
        const data = Object.assign(this.commonData, {
          title,
          fileName: file.link,
          downloadRequestOptions: {
            originalFileName: file.name,
          },
          metadata: {
            uploadStatus: app.uploadStatus.UPLOAD,
          },
          progress: {},
        });
        const model = new app.namespace.Model.UploadQueueModel(data, {
          urlUpload: true,
        });
        app.UploadQueue.upload(model);
        uqModels.push(model);
      }

      this.closeModal({
        uqModels,
        redirectToAssetGallery: true,
      });
    },

    onBrowse() {
      switch (this.provider) {
        case 'dropbox':
          this.browseDropbox();
          break;
        case 'onedrive':
          this.browseOnedrive();
          break;
      }
    },

    browseDropbox() {
      window.Dropbox.choose({
        success: (files) => {
          const concatArray = this.model.files;
          this.model.files.concat(files).forEach((item) => {
            if (
              !this.model.files.find((modelItem) => item.id === modelItem.id)
            ) {
              concatArray.push(item);
            }
          });
          this.model.files = concatArray;
        },

        linkType: 'direct',

        multiselect: true,

        extensions: tk.context._supportedFileExtensions.map((v) => {
          return '.' + v.toLowerCase();
        }),

        folderselect: false, // or true
      });
    },

    browseOnedrive() {
      const odOptions = {
        clientId: app.getOnedriveClientId(),
        action: 'download',
        multiSelect: true,
        advanced: {
          redirectUri:
            location.protocol +
            '//' +
            location.host +
            '/resources/html/onedriveRedirect.html',
          queryParameters:
            'select=id,name,size,file,folder,photo,@microsoft.graph.downloadUrl',
          /* display folder and files with  permitted extensions only */
          filter:
            'folder, .' +
            tk.context._supportedFileExtensions.join(',.').toLowerCase(),
        },
        success: (res) => {
          if (res.value.length) {
            _.each(res.value, (v) => {
              const file = {
                id: v['id'],
                link: v['@microsoft.graph.downloadUrl'],
                name: v['name'],
                bytes: v['size'],
              };
              if (
                !this.model.files.find((modelItem) => file.id === modelItem.id)
              ) {
                this.model.files.push(file);
              }
            });
          }
        },
        error: (error) => {
          app.dialog.error({
            type: app.dialog.types.ERROR_1,
            title: `${app.culture.onedriveDialog.errorTitle}`,
            showContactCsm: true,
          });
          console.error(error);
        },
      };
      window.OneDrive.open(odOptions);
    },

    onRemoveFileRecord({ file }) {
      const i = this.model.files.indexOf(file);
      if (i >= 0) {
        this.model.files.splice(i, 1);
      }
    },
  },
};
</script>
